import {
  normalizeCountryCode,
  normalizeStateCode,
} from '@/utils/countryRegions';
export {
  normalizeCountryCode,
  normalizeStateCode,
  isValidStateForCountry,
} from '@/utils/countryRegions';
import dayjs from 'dayjs';

import { MAX_PRODUCT_QUANTITY } from '@/utils/formatQuantity';
import type { CourierData } from '../../apis/lookup';
import type {
  ShipmentLocationData,
  SingleShipmentDataExtended,
} from '../../apis/types';

/** Mirrors the backend constant in `shipment.services.fedex_service.FEDEX_COURIER_ID`.
 *  Used when promoting a shell shipment after a successful FedEx Book & Generate
 *  Label step. */
export const FEDEX_COURIER_ID = '3a4ceea5-3bf5-493e-9c8b-b9d00e5bc4ac';

/** Mirrors the backend constant in `shipment.services.ups_service.UPS_COURIER_ID`. */
export const UPS_COURIER_ID = 'ef1df09a-148f-4f14-8b79-41210c2b21f9';

export interface ShellPartyValues {
  /** Contact person name. */
  name?: string;
  /** Company / site name (prefilled from location.name). */
  company?: string;
  phone?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  residential?: boolean;
}

function normField(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

/** Subscriber digits only — trailing ``ext`` / ``x`` / ``extension`` is dropped. */
export function phoneDigitsWithoutExtension(raw?: string | null): string {
  const trimmed = String(raw ?? '')
    .trim()
    .replace(/(?:ext(?:ension)?\.?|x)\s*\d+\s*$/i, '');
  return trimmed.replace(/\D/g, '').slice(-15);
}

/** US zip matching: compare 5-digit base when present (handles 02108-1234 vs 02108). */
function normZip(v: unknown): string {
  const raw = normField(v);
  const match = raw.match(/^(\d{5})/);
  return match ? match[1] : raw;
}

/** FedEx US quotes prefer 5-digit ZIP; strip ZIP+4 (`94025-7097` → `94025`). */
export function normalizePostalCode(
  value: string | null | undefined,
  country?: string | null
): string | undefined {
  const stripped = normField(value);
  if (!stripped) return undefined;
  const cc = normalizeCountryCode(country) || 'US';
  if (cc === 'US') {
    return normZip(stripped) || stripped;
  }
  if (cc === 'CA') {
    // FedEx accepts A1A1A1; strip spaces/hyphens for consistency.
    return stripped.replace(/[\s-]/g, '').toUpperCase();
  }
  return stripped;
}

/**
 * Pull suite/unit/apt designators out of a street line. Mirrors
 * ``extract_unit_designator`` in ``shipment_detail.py``; also keeps a
 * trailing ``& …`` segment (e.g. "Suite 130 & 140").
 *
 * Rejects matches where the token after the keyword is a street suffix so
 * names like "Apartment Road" / "Unit Avenue" stay on line 1.
 */
const UNIT_KEYWORD_RE =
  /^(?:suite|ste|unit|apt|apartment|bldg|building|floor|fl|rm|room)\.?$/i;
const UNIT_DESIGNATOR_RE =
  /\b(?:suite|ste|unit|apt|apartment|bldg|building|floor|fl|rm|room)\.?(?:\s+#?\s*|\s*#\s*)[A-Za-z0-9][A-Za-z0-9\-/]*(?:\s*&\s*[A-Za-z0-9][A-Za-z0-9\-/]*)*|#\s*[A-Za-z0-9][A-Za-z0-9\-/]*/i;

/** Common USPS street-type words — not valid unit identifiers. */
const STREET_TYPE_TOKENS = new Set([
  'road',
  'rd',
  'street',
  'st',
  'avenue',
  'ave',
  'boulevard',
  'blvd',
  'drive',
  'dr',
  'lane',
  'ln',
  'way',
  'court',
  'ct',
  'circle',
  'cir',
  'place',
  'pl',
  'terrace',
  'ter',
  'highway',
  'hwy',
  'parkway',
  'pkwy',
  'trail',
  'trl',
  'path',
  'loop',
  'alley',
  'aly',
  'row',
  'square',
  'sq',
  'pike',
  'pass',
  'crossing',
  'xing',
  'expressway',
  'expy',
  'freeway',
  'fwy',
  'turnpike',
  'tpke',
]);

function unitValueLooksLikeStreetType(unit: string): boolean {
  const withoutHash = unit.replace(/^#\s*/, '').trim();
  const tokens = withoutHash.split(/\s+/).filter(Boolean);
  // "Apartment Road" → keyword + street type
  if (tokens.length >= 2 && UNIT_KEYWORD_RE.test(tokens[0])) {
    return STREET_TYPE_TOKENS.has(tokens[1].toLowerCase());
  }
  return false;
}

export function extractUnitDesignator(address?: string | null): string {
  if (!address) return '';
  const matches = address.matchAll(new RegExp(UNIT_DESIGNATOR_RE.source, 'gi'));
  for (const match of matches) {
    const unit = match[0]?.trim() ?? '';
    if (unit && !unitValueLooksLikeStreetType(unit)) return unit;
  }
  return '';
}

/** Split a single street line into address line 1 + suite/unit line 2. */
export function splitStreetAndUnit(line: string): {
  street: string;
  street2?: string;
} {
  const trimmed = line.trim();
  if (!trimmed) return { street: '' };
  const unit = extractUnitDesignator(trimmed);
  if (!unit) return { street: trimmed };
  const idx = trimmed.toLowerCase().indexOf(unit.toLowerCase());
  if (idx < 0) return { street: trimmed };
  const street = `${trimmed.slice(0, idx)}${trimmed.slice(idx + unit.length)}`
    .replace(/[,\s]+/g, ' ')
    .trim();
  return street ? { street, street2: unit } : { street: trimmed };
}

/**
 * Street segments from a raw US-style address, excluding trailing
 * ``City, ST ZIP`` when present so comma-separated suites are kept.
 */
function streetPartsFromRawAddress(raw: string): string[] {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    const last = parts[parts.length - 1];
    const stateZip = last.match(
      /^([A-Za-z][A-Za-z\s.]*?)\s+(\d{5}(?:-\d{4})?)$/
    );
    if (stateZip) {
      const state = normalizeStateCode(stateZip[1]);
      if (state && state.length === 2) {
        return parts.slice(0, -2);
      }
    }
  }
  return parts.length ? parts : [raw];
}

/**
 * Best-effort street lines from parsed address, falling back to the raw
 * address string's first comma-separated segment (city/state/zip stripped).
 * Suite/unit goes to ``street2`` when present as SubaddressIdentifier or
 * embedded in the street line / raw address.
 */
export function streetLinesFromShipmentLocation(
  location?: ShipmentLocationData
): { street?: string; street2?: string } {
  if (!location) return {};
  const parsed = location.parsed_address ?? {};
  const subaddress = normField(parsed.SubaddressIdentifier);
  const fromParsed = [parsed.AddressNumber, parsed.StreetName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fromParsed) {
    if (subaddress) {
      // Strip only a real unit designator (Suite/Unit/…), never a numeric
      // substring of the street number ("4" must not turn "400 Main St"
      // into "00 Main St").
      const { street } = splitStreetAndUnit(fromParsed);
      return {
        street: street || fromParsed,
        street2: subaddress,
      };
    }
    return splitStreetAndUnit(fromParsed);
  }

  const raw = normField(location.address);
  if (!raw) return subaddress ? { street2: subaddress } : {};
  // Keep leading suite segments: "100 Main St, Suite 4, Austin, TX 78701"
  // → streetParts ["100 Main St", "Suite 4"] (city/state/zip stripped).
  const streetParts = streetPartsFromRawAddress(raw);
  const first = streetParts[0] || raw;
  if (subaddress) {
    const leadingUnit = extractUnitDesignator(first);
    // Unit-first + parsed identifier: "Suite 4, 100 Main St, …" with
    // SubaddressIdentifier "4" must not return street "Suite 4".
    if (
      streetParts.length >= 2 &&
      leadingUnit &&
      leadingUnit.toLowerCase() === first.toLowerCase()
    ) {
      const streetLine = streetParts[1]?.trim() ?? '';
      return streetLine
        ? { street: streetLine, street2: subaddress }
        : { street: first, street2: subaddress };
    }
    const { street } = splitStreetAndUnit(first);
    return { street: street || first, street2: subaddress };
  }
  if (streetParts.length >= 2) {
    const leadingUnit = extractUnitDesignator(streetParts[0]);
    // Unit-first: "Suite 4, 100 Main St, Austin, TX 78701"
    if (
      leadingUnit &&
      leadingUnit.toLowerCase() === streetParts[0].toLowerCase()
    ) {
      const streetLine = streetParts[1]?.trim() ?? '';
      return streetLine
        ? { street: streetLine, street2: streetParts[0] }
        : { street: first };
    }
    const unitSeg = extractUnitDesignator(streetParts[1]);
    if (unitSeg && unitSeg.toLowerCase() === streetParts[1].toLowerCase()) {
      return { street: first, street2: streetParts[1] };
    }
  }
  // Safe fallback: first segment only. Joining remaining parts can pull
  // city/postal into street when the raw string is not "street, City, ST ZIP"
  // (e.g. "100 Main St, Austin TX 78701").
  return splitStreetAndUnit(first);
}

/**
 * Best-effort street line from parsed address, falling back to the raw
 * address string's first comma-separated segment (city/state/zip stripped).
 */
export function streetFromShipmentLocation(
  location?: ShipmentLocationData
): string | undefined {
  return streetLinesFromShipmentLocation(location).street;
}

/**
 * When LocationV2Parsed failed (empty PlaceName/StateName/ZipCode), recover
 * city/state/zip from a US-style raw address:
 *   "6520 US-301 Unit 105, Riverview, FL 33578"
 * Detail views already fall back to the full address string; Quote/Return
 * prefill must populate discrete fields the same way.
 *
 * Only accepts a trailing ``ST ZIP`` / ``StateName ZIP`` segment so we do not
 * invent state codes from unrelated trailing tokens (e.g. "Building A 12345").
 */
export function cityStateZipFromRawAddress(raw?: string | null): {
  city?: string;
  state?: string;
  postalCode?: string;
} {
  const address = normField(raw);
  if (!address) return {};
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  // Need at least "street, City, ST ZIP"
  if (parts.length < 3) return {};

  const last = parts[parts.length - 1];
  // "FL 33578" / "North Carolina 27529"
  const stateZip = last.match(/^([A-Za-z][A-Za-z\s.]*?)\s+(\d{5}(?:-\d{4})?)$/);
  if (!stateZip) return {};

  const state = normalizeStateCode(stateZip[1]);
  // normalizeStateCode returns the raw string for unknowns — reject those.
  if (!state || state.length !== 2) return {};

  const postalCode = normalizePostalCode(stateZip[2], 'US');
  const city = parts[parts.length - 2];
  if (!city || !postalCode) return {};

  return { city, state, postalCode };
}

/** Convert a shipment location with parsed_address into the address party
 *  fields the FedEx/UPS Quote / Book drawers expect. The location's `name`
 *  becomes the company name when available; contact stays blank for the user.
 *  Phone is not stored on a shell shipment. */
export function partyFromShipmentLocation(
  location?: ShipmentLocationData
): ShellPartyValues {
  if (!location) return { country: 'US' };
  const parsed = location.parsed_address ?? {};
  const country = normalizeCountryCode(parsed.CountryName) || 'US';
  const fromRaw = cityStateZipFromRawAddress(
    typeof location.address === 'string' ? location.address : undefined
  );
  const { street, street2 } = streetLinesFromShipmentLocation(location);
  return {
    company: location.name || undefined,
    street: street || undefined,
    street2: street2 || undefined,
    city: normField(parsed.PlaceName) || fromRaw.city || undefined,
    state:
      normalizeStateCode(parsed.StateName, country) ||
      fromRaw.state ||
      undefined,
    postalCode:
      normalizePostalCode(parsed.ZipCode, country) ||
      fromRaw.postalCode ||
      undefined,
    country,
  };
}
export function isFedExShipment(
  shipment?: SingleShipmentDataExtended | null
): boolean {
  if (!shipment?.courier) return false;
  const courier = shipment.courier as { id?: string; title?: string };
  if (courier.id && String(courier.id) === FEDEX_COURIER_ID) return true;
  const title = (courier.title || '').trim().toLowerCase();
  return title === 'fedex' || title.startsWith('fedex ');
}

export function isUPSShipment(
  shipment?: SingleShipmentDataExtended | null
): boolean {
  if (!shipment?.courier) return false;
  const courier = shipment.courier as { id?: string; title?: string };
  if (courier.id && String(courier.id) === UPS_COURIER_ID) return true;
  const title = (courier.title || '').trim().toLowerCase();
  return title === 'ups' || title.startsWith('ups ');
}

/** True when the shipment courier is supported by Quote & Book. */
export function isQuoteableCarrierShipment(
  shipment?: SingleShipmentDataExtended | null
): boolean {
  return isFedExShipment(shipment) || isUPSShipment(shipment);
}

/** Pre-pickup / pre-ops statuses where shell Quote & Book still make sense.
 *  Includes CL/WC equivalents of PENDING / AWAITING_PICKUP.
 *  NOT_FOUND is included because status_service falls shells back to
 *  "Not Found", while pizza tracker still displays that as "Awaiting Pickup". */
const SHELL_QUOTE_BOOK_STATUS_ALLOWLIST = new Set([
  'PENDING',
  'AWAITING_PICKUP',
  'RATED',
  'INCOMPLETE',
  'DATA_LOADING',
  'NOT_FOUND',
]);

/** Normalize shipment progress status for allowlist checks.
 *  Detail API display labels use spaces (``AWAITING PICKUP``); raw/DB forms
 *  use underscores or hyphens — collapse all to underscores. */
export function normalizeShipmentProgressStatus(
  status?: string | null
): string {
  return String(status ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-_\s]+/g, '_');
}

/**
 * Whether a shell shipment should show Quote / Book in the detail header.
 * Hide once pickup has happened or status is past pre-pickup allowlist
 * (PENDING / AWAITING_PICKUP / NOT_FOUND and CL equivalents). Unknown/empty
 * statuses fail closed (hide CTAs).
 */
export function canQuoteOrBookShell(
  shipment?: SingleShipmentDataExtended | null
): boolean {
  if (!shipment?.is_shell) return false;

  const pickup = shipment.actual_pickup_date;
  if (pickup != null && String(pickup).trim() !== '') return false;

  const status = normalizeShipmentProgressStatus(shipment.status);
  if (!status) return false;
  return SHELL_QUOTE_BOOK_STATUS_ALLOWLIST.has(status);
}

/** True when from/to have enough parsed address fields for a carrier quote.
 *  Useful for prefill quality checks; users can complete missing fields in the
 *  drawer when prefill is incomplete. */
export function hasQuoteableAddresses(
  shipment?: SingleShipmentDataExtended | null
): boolean {
  if (!shipment) return false;
  const ok = (loc?: ShipmentLocationData): boolean => {
    if (!loc) return false;
    const party = partyFromShipmentLocation(loc);
    return Boolean(
      party.street && party.city && party.state && party.postalCode
    );
  };
  return ok(shipment.from_location) && ok(shipment.to_location);
}

/** Parse shipment.volume "XL x YW x ZH" into integer inches, or nulls. */
export function parseVolumeToDimensions(volume?: string | null): {
  length?: number;
  width?: number;
  height?: number;
} {
  if (!volume || typeof volume !== 'string') return {};
  const match = volume
    .trim()
    .match(
      /(\d+(?:\.\d+)?)\s*[lL]\s*[xX]\s*(\d+(?:\.\d+)?)\s*[wW]\s*[xX]\s*(\d+(?:\.\d+)?)\s*[hH]/i
    );
  if (!match) return {};
  const length = Math.round(Number(match[1]));
  const width = Math.round(Number(match[2]));
  const height = Math.round(Number(match[3]));
  if (!(length > 0 && width > 0 && height > 0)) return {};
  return { length, width, height };
}

/** Convert kg to pounds for the quote form (FedEx payload uses LB). */
export function weightKgToLb(
  weightKg?: number | string | null
): number | undefined {
  if (weightKg == null || weightKg === '') return undefined;
  const n = Number(weightKg);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.round(n * 2.20462 * 100) / 100;
}

/** Convert pounds back to kg (inverse of weightKgToLb) for storing weight_kg. */
export function weightLbToKg(
  weightLb?: number | string | null
): number | undefined {
  if (weightLb == null || weightLb === '') return undefined;
  const n = Number(weightLb);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.round((n / 2.20462) * 100) / 100;
}

/**
 * Soft-link return reference: RET-{outboundRef}, then RET-{outboundRef}-2, …
 * Truncates to 30 chars (FedEx customer reference limit / ZoomLogi form max).
 */
export function nextReturnReference(
  outboundRef: string | null | undefined,
  existingRefs: Iterable<string> = []
): string {
  const baseRaw = normField(outboundRef) || 'RETURN';
  const existing = new Set(
    Array.from(existingRefs).map((r) => normField(r).toUpperCase())
  );
  const clip = (s: string): string => s.slice(0, 30);
  let candidate = clip(`RET-${baseRaw}`);
  if (!existing.has(candidate.toUpperCase())) return candidate;
  for (let i = 2; i < 1000; i += 1) {
    const suffix = `-${i}`;
    const prefixBudget = Math.max(1, 30 - suffix.length);
    candidate = clip(`RET-${baseRaw}`.slice(0, prefixBudget) + suffix);
    if (!existing.has(candidate.toUpperCase())) return candidate;
  }
  return clip(`RET-${baseRaw}-${Date.now().toString(36)}`);
}

export interface ShellToQuoteFormValues {
  pickup: ShellPartyValues;
  delivery: ShellPartyValues;
  reference?: string;
  shipDate?: ReturnType<typeof dayjs>;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
}

/** Map a shell shipment into initialValues for QuoteShipmentDrawer.
 *  Weight is intentionally left blank — the shell create form doesn't
 *  capture weight, so the user fills it in on the Quote step. */
export function shellToQuoteFormValues(
  shipment: SingleShipmentDataExtended | undefined
): ShellToQuoteFormValues {
  if (!shipment) {
    return {
      pickup: { country: 'US' },
      delivery: { country: 'US' },
      shipDate: dayjs(),
    };
  }
  const shipDateSource =
    shipment.customer_preferred_pickup_date ||
    shipment.scheduled_pickup_date ||
    shipment.shipment_date ||
    undefined;
  return {
    pickup: partyFromShipmentLocation(shipment.from_location),
    delivery: partyFromShipmentLocation(shipment.to_location),
    reference: shipment.reference_number || undefined,
    shipDate: shipDateSource ? dayjs(shipDateSource) : dayjs(),
  };
}

/**
 * Prefill Quote drawer for a FedEx return label: flipped origin/dest,
 * RET-{ref} reference, today's ship date, weight + dims when available.
 */
export function outboundToReturnQuoteFormValues(
  shipment: SingleShipmentDataExtended | undefined,
  existingReturnRefs: Iterable<string> = []
): ShellToQuoteFormValues {
  if (!shipment) {
    return {
      pickup: { country: 'US' },
      delivery: { country: 'US' },
      shipDate: dayjs(),
      reference: nextReturnReference(undefined, existingReturnRefs),
    };
  }
  const dims = parseVolumeToDimensions(shipment.volume);
  return {
    // Return direction: package is at outbound dest → goes back to outbound origin
    pickup: partyFromShipmentLocation(shipment.to_location),
    delivery: partyFromShipmentLocation(shipment.from_location),
    reference: nextReturnReference(
      shipment.reference_number,
      existingReturnRefs
    ),
    shipDate: dayjs(),
    weight: weightKgToLb(shipment.weight_kg),
    ...dims,
  };
}

/**
 * Build CreateNewShipment ``products`` payload from outbound shipment lines.
 * Mirrors auto-return ingest (product_id + quantity + serial_number).
 */
export function productsPayloadFromOutboundLines(
  lines: Array<{
    product_id?: string | null;
    quantity?: number | null;
    serial_preview?: string[] | null;
    serial_number?: string | null;
  }>
): Array<{ product_id: string; quantity: number; serial_number: string }> {
  const byProduct = new Map<
    string,
    { product_id: string; quantity: number; serial_number: string }
  >();
  for (const line of lines) {
    const productId = normField(line.product_id);
    if (!productId) continue;
    const qty = Math.max(1, Number(line.quantity) || 1);
    const serial =
      normField(line.serial_number) ||
      (Array.isArray(line.serial_preview)
        ? line.serial_preview
            .map((s) => normField(s))
            .filter(Boolean)
            .join(', ')
        : '');
    const existing = byProduct.get(productId);
    if (existing) {
      existing.quantity += qty;
      if (serial) {
        existing.serial_number = existing.serial_number
          ? `${existing.serial_number}, ${serial}`
          : serial;
      }
    } else {
      byProduct.set(productId, {
        product_id: productId,
        quantity: qty,
        serial_number: serial,
      });
    }
  }
  return Array.from(byProduct.values());
}

export interface QuoteSelectedProduct {
  id: string;
  quantity: number;
  serial_number?: string;
  name?: string;
}

/** Map outbound lines into Quote product-row state (id/qty/serial + optional name). */
export function selectedProductsFromOutboundLines(
  lines: Array<{
    product_id?: string | null;
    name?: string | null;
    quantity?: number | null;
    serial_preview?: string[] | null;
    serial_number?: string | null;
  }>
): QuoteSelectedProduct[] {
  const names = new Map<string, string>();
  for (const line of lines) {
    const id = normField(line.product_id);
    const name = normField(line.name);
    if (id && name && !names.has(id)) names.set(id, name);
  }
  return productsPayloadFromOutboundLines(lines).map((row) => ({
    id: row.product_id,
    quantity: row.quantity,
    ...(row.serial_number ? { serial_number: row.serial_number } : {}),
    ...(names.get(row.product_id) ? { name: names.get(row.product_id) } : {}),
  }));
}

/** Keep qty/serials when the product multi-select remaps selected ids. */
export function mergeProductSelection(
  finalIds: string[],
  existing: Array<{ id: string; quantity: number; serial_number?: string }>
): Array<{ id: string; quantity: number; serial_number?: string }> {
  return finalIds.map((id) => {
    const prev = existing.find((sp) => sp.id === id);
    return {
      id,
      quantity: prev?.quantity || 1,
      ...(prev?.serial_number ? { serial_number: prev.serial_number } : {}),
    };
  });
}

export function productsPayloadFromSelectedRows(
  rows: Array<{ id: string; quantity: number; serial_number?: string }>
): Array<{ product_id: string; quantity: number; serial_number?: string }> {
  return rows
    .filter((row) => row.id && row.id !== 'all_products')
    .map((row) => {
      const serial = normField(row.serial_number);
      return {
        product_id: row.id,
        quantity: Math.max(1, Number(row.quantity) || 1),
        ...(serial ? { serial_number: serial } : {}),
      };
    });
}

export type ProductsPrefillStatus = 'idle' | 'loading' | 'ready' | 'failed';

/** Book-time fetch only when return prefill has not settled as ready.
 * Skip after any user product mutation so a cleared selection is not restored. */
export function shouldFallbackReturnProductsFetch(
  isReturnMode: boolean,
  prefillStatus: ProductsPrefillStatus,
  selectedCount: number,
  productsTouched = false
): boolean {
  return (
    isReturnMode &&
    selectedCount === 0 &&
    prefillStatus !== 'ready' &&
    !productsTouched
  );
}

export function quotePromoteTargetId(
  isReturnMode: boolean,
  shellShipmentId?: string | null,
  refMatchedShipmentId?: string | null
): string | null {
  if (isReturnMode) return null;
  return shellShipmentId ?? refMatchedShipmentId ?? null;
}

export function shouldShowQuoteTeamsProducts(
  isReturnMode: boolean,
  refStatus: string,
  promoteTargetId: string | null
): boolean {
  if (isReturnMode) return true;
  if (refStatus === 'checking') return false;
  return !promoteTargetId;
}

export function applyProductQuantityDelta<
  T extends { id: string; quantity: number },
>(
  products: T[],
  productId: string,
  delta: number
): { next: T[]; error?: 'min' | 'max' } {
  const idx = products.findIndex((p) => p.id === productId);
  if (idx < 0 || products[idx].id === 'all_products') {
    return { next: products };
  }
  const newQuantity = products[idx].quantity + delta;
  if (newQuantity < 1) return { next: products, error: 'min' };
  if (newQuantity > MAX_PRODUCT_QUANTITY)
    return { next: products, error: 'max' };
  return {
    next: products.map((p, i) =>
      i === idx ? { ...p, quantity: newQuantity } : p
    ),
  };
}

// --- Book-from-shell helpers (WC + Cryoport) ---

export interface WcTemplateMatchRow {
  id: string;
  address1?: string | null;
  city?: string | null;
  postal_code?: string | null;
  created_at?: string | null;
}

export interface CryoTemplateMatchRow {
  id: string;
  cryo_street_address_line_one?: string | null;
  cryo_city?: string | null;
  cryo_zip_code?: string | null;
  created_at?: string | null;
}

export interface WcSiteFields {
  address1?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
}

export interface CryoSiteFields {
  street_address_line_one?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country_code?: string;
}

export interface ShellBookPrefillFormValues {
  wc_origin_address1?: string;
  wc_origin_city?: string;
  wc_origin_state_province?: string;
  wc_origin_postal_code?: string;
  wc_origin_country?: string;
  wc_dest_address1?: string;
  wc_dest_city?: string;
  wc_dest_state_province?: string;
  wc_dest_postal_code?: string;
  wc_dest_country?: string;
  cryo_origin_street_address_line_one?: string;
  cryo_origin_city?: string;
  cryo_origin_state?: string;
  cryo_origin_zip_code?: string;
  cryo_origin_country_code?: string;
  cryo_dest_street_address_line_one?: string;
  cryo_dest_city?: string;
  cryo_dest_state?: string;
  cryo_dest_zip_code?: string;
  cryo_dest_country_code?: string;
  wc_reference_code?: string;
  cryo_leg2_shipment_reference?: string;
}

export type ShellBookTransportProvider = 'WORLD_COURIER' | 'CRYOPORT';

function wcStreetFromLocation(location?: ShipmentLocationData): string {
  if (!location) return '';
  const parsed = location.parsed_address ?? {};
  const streetParts = [parsed.AddressNumber, parsed.StreetName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return normField(streetParts || location.address);
}

function cryoStreetFromLocation(location?: ShipmentLocationData): string {
  return wcStreetFromLocation(location);
}

function pickNewestTemplate<
  T extends { id: string; created_at?: string | null },
>(matches: T[]): T {
  return [...matches].sort((a, b) => {
    const aTs = a.created_at ? Date.parse(a.created_at) : 0;
    const bTs = b.created_at ? Date.parse(b.created_at) : 0;
    return bTs - aTs;
  })[0];
}

/** Address-only WC site fields from a shell LocationV2 (no contacts). */
export function shellLocationToWcSiteFields(
  location?: ShipmentLocationData
): WcSiteFields {
  if (!location) return { country: 'USA' };
  const parsed = location.parsed_address ?? {};
  const street = wcStreetFromLocation(location);
  return {
    address1: street || undefined,
    city: normField(parsed.PlaceName) || undefined,
    state_province: normField(parsed.StateName) || undefined,
    postal_code: normField(parsed.ZipCode) || undefined,
    country: 'USA',
  };
}

/** Address-only Cryoport site fields from a shell LocationV2 (no contacts / time_zone). */
export function shellLocationToCryoSiteFields(
  location?: ShipmentLocationData
): CryoSiteFields {
  if (!location) return { country_code: 'US' };
  const parsed = location.parsed_address ?? {};
  const street = cryoStreetFromLocation(location);
  return {
    street_address_line_one: street || undefined,
    city: normField(parsed.PlaceName) || undefined,
    state: normField(parsed.StateName) || undefined,
    zip_code: normField(parsed.ZipCode) || undefined,
    country_code: 'US',
  };
}

export function findBestWcTemplateForLocation(
  location: ShipmentLocationData | undefined,
  templates: WcTemplateMatchRow[]
): { templateId: string } | undefined {
  if (!location) return undefined;
  const street = wcStreetFromLocation(location);
  const city = normField(location.parsed_address?.PlaceName);
  const zip = normZip(location.parsed_address?.ZipCode);
  if (!street || !city || !zip) return undefined;

  const matches = templates.filter(
    (t) =>
      normField(t.address1) === street &&
      normField(t.city) === city &&
      normZip(t.postal_code) === zip
  );
  if (matches.length === 0) return undefined;
  return { templateId: pickNewestTemplate(matches).id };
}

export function findBestCryoTemplateForLocation(
  location: ShipmentLocationData | undefined,
  templates: CryoTemplateMatchRow[]
): { templateId: string } | undefined {
  if (!location) return undefined;
  const street = cryoStreetFromLocation(location);
  const city = normField(location.parsed_address?.PlaceName);
  const zip = normZip(location.parsed_address?.ZipCode);
  if (!street || !city || !zip) return undefined;

  const matches = templates.filter(
    (t) =>
      normField(t.cryo_street_address_line_one) === street &&
      normField(t.cryo_city) === city &&
      normZip(t.cryo_zip_code) === zip
  );
  if (matches.length === 0) return undefined;
  return { templateId: pickNewestTemplate(matches).id };
}

function assignWcSideFields(
  formValues: ShellBookPrefillFormValues,
  side: 'origin' | 'destination',
  fields: WcSiteFields
): void {
  const prefix = side === 'origin' ? 'wc_origin' : 'wc_dest';
  if (fields.address1) {
    formValues[`${prefix}_address1` as keyof ShellBookPrefillFormValues] =
      fields.address1;
  }
  if (fields.city) {
    formValues[`${prefix}_city` as keyof ShellBookPrefillFormValues] =
      fields.city;
  }
  if (fields.state_province) {
    formValues[`${prefix}_state_province` as keyof ShellBookPrefillFormValues] =
      fields.state_province;
  }
  if (fields.postal_code) {
    formValues[`${prefix}_postal_code` as keyof ShellBookPrefillFormValues] =
      fields.postal_code;
  }
  if (fields.country) {
    formValues[`${prefix}_country` as keyof ShellBookPrefillFormValues] =
      fields.country;
  }
}

function assignCryoSideFields(
  formValues: ShellBookPrefillFormValues,
  side: 'origin' | 'destination',
  fields: CryoSiteFields
): void {
  const prefix = side === 'origin' ? 'cryo_origin' : 'cryo_dest';
  if (fields.street_address_line_one) {
    formValues[
      `${prefix}_street_address_line_one` as keyof ShellBookPrefillFormValues
    ] = fields.street_address_line_one;
  }
  if (fields.city) {
    formValues[`${prefix}_city` as keyof ShellBookPrefillFormValues] =
      fields.city;
  }
  if (fields.state) {
    formValues[`${prefix}_state` as keyof ShellBookPrefillFormValues] =
      fields.state;
  }
  if (fields.zip_code) {
    formValues[`${prefix}_zip_code` as keyof ShellBookPrefillFormValues] =
      fields.zip_code;
  }
  if (fields.country_code) {
    formValues[`${prefix}_country_code` as keyof ShellBookPrefillFormValues] =
      fields.country_code;
  }
}

/** Phase A (shell): WC origin/dest address-only fields + reference code.
 *  Never matches or applies a WC template — template matching is deferred to
 *  Phase B once the user has selected a Project (site). */
export function shellToWcAddressOnlyFormValues(
  shipment: SingleShipmentDataExtended | undefined
): ShellBookPrefillFormValues {
  const formValues: ShellBookPrefillFormValues = {};
  if (!shipment) return formValues;
  assignWcSideFields(
    formValues,
    'origin',
    shellLocationToWcSiteFields(shipment.from_location)
  );
  assignWcSideFields(
    formValues,
    'destination',
    shellLocationToWcSiteFields(shipment.to_location)
  );
  const ref = shipment.reference_number?.trim();
  if (ref) {
    formValues.wc_reference_code = ref;
  }
  return formValues;
}

export interface ShellToCryoFormValuesResult {
  formValues: ShellBookPrefillFormValues;
  cryoOriginTemplateId?: string;
  cryoDestTemplateId?: string;
}

/** Phase A (shell): Cryoport site matching + address fallback + reference.
 *  Cryoport has no project/site dimension, so this stays a one-time prefill. */
export function shellToCryoFormValues(
  shipment: SingleShipmentDataExtended | undefined,
  cryoTemplates: CryoTemplateMatchRow[]
): ShellToCryoFormValuesResult {
  const result: ShellToCryoFormValuesResult = { formValues: {} };
  if (!shipment) return result;

  const cryoOriginMatch = findBestCryoTemplateForLocation(
    shipment.from_location,
    cryoTemplates
  );
  const cryoDestMatch = findBestCryoTemplateForLocation(
    shipment.to_location,
    cryoTemplates
  );

  if (cryoOriginMatch) {
    result.cryoOriginTemplateId = cryoOriginMatch.templateId;
  } else {
    assignCryoSideFields(
      result.formValues,
      'origin',
      shellLocationToCryoSiteFields(shipment.from_location)
    );
  }

  if (cryoDestMatch) {
    result.cryoDestTemplateId = cryoDestMatch.templateId;
  } else {
    assignCryoSideFields(
      result.formValues,
      'destination',
      shellLocationToCryoSiteFields(shipment.to_location)
    );
  }

  const ref = shipment.reference_number?.trim();
  if (ref) {
    result.formValues.cryo_leg2_shipment_reference = ref;
  }

  return result;
}

function matchesTransportProviderName(
  courierName: string,
  provider: ShellBookTransportProvider
): boolean {
  const normalized = courierName.trim().toLowerCase();
  if (provider === 'WORLD_COURIER') {
    return normalized.includes('world courier');
  }
  return normalized.includes('cryoport');
}

/** Resolve company courier FK for promote payload from DDL rows. */
export function resolveCourierIdForTransportProvider(
  couriersList: CourierData[],
  provider: ShellBookTransportProvider
): string | undefined {
  const matches = couriersList.filter((row) =>
    matchesTransportProviderName(row.courier_name ?? '', provider)
  );
  if (matches.length === 0) return undefined;
  if (matches.length > 1) {
    console.warn(
      `resolveCourierIdForTransportProvider: ambiguous ${provider} match (${matches.length} rows)`
    );
  }
  const row = matches[0];
  // Courier table UUID (FK) — not CompanyCourier row id (see CreateShipmentDrawer).
  return row.courier ?? row.courier_id;
}
